import {
  RefObject,
  FC,
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useTracking } from 'react-tracking';
import { Textfield } from '@simplywallst/ui-core';
import SearchFieldPostfix from '@components/SearchFieldPostfix';
import {
  SearchIcon,
  SearchDrop,
  ScrollPane,
  SearchResults,
  SearchWrapper,
} from './styled';
import type {
  Status,
  SearchResults as SearchResultsType,
} from '@/hooks/useSearch';
import { useClickoutisde } from '@/hooks/useClickoutisde';
import Popper from '@components/Tooltip/components/Popper';
import { useKeys } from '@/hooks/useKeys';

interface CompanyData {
  scores: number[];
  companyId: string;
  companyName: string;
  tickerSymbol: string;
  canonicalUrl: string;
  uniqueSymbol: string;
}

export interface Props {
  light?: boolean;
  name?: string;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  showOnSelect?: keyof Omit<CompanyData, 'scores'>;
  forwardedRef?: RefObject<HTMLInputElement>;
  dropdownOptionWidth?: number; // TODO: there's a better way, but this'll do for now
  onFocus?: () => void;
  onSelect?: (key: string, entity: CompanyData) => void;
  onSearchResult?: (searchResults: SearchResultsType) => void;
  /** */
  query?: string;
  status?: Status;
  onChange?: (value: string) => void;
  onOpen?: (open: boolean, ref: RefObject<HTMLInputElement>) => void;
  onClose?: () => void;
}

export const DesktopSearch: FC<Props> = (props) => {
  const { trackEvent } = useTracking();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const childRef = useRef<HTMLDivElement>(null);
  const animationDuration = 300;

  const handleClose = () => {
    if (typeof props.onClose === 'function') {
      props.onClose();
    }
  };
  useClickoutisde([containerRef, childRef], () => {
    debugger;
    handleBlur();
    trackEvent({ action: 'blur', query: value });
    handleClose();
  });

  /** stopPropagation is being derpy so this variable exist */
  const supressFocusEvent = useRef(false);

  const [value, setValue] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);

  useEffect(() => {
    if (typeof props.onOpen === 'function') {
      props.onOpen(showSearchResults, inputRef);
    }
  }, [showSearchResults, props.onOpen, inputRef]);

  useEffect(() => {
    if (typeof props.onChange === 'function') {
      props.onChange(value);
    }
    setShowSearchResults(value !== '');
  }, [value, props.onChange]);

  const handleBlur = useCallback(() => {
    if (showSearchResults) {
      setShowSearchResults(false);
    }
  }, [showSearchResults]);

  const keys = useMemo(() => ['Tab', 'Enter'] as const, []);
  useKeys({
    targetKeys: keys,
    targetRef: inputRef.current,
    callback: handleBlur,
  });

  const handleReset = () => {
    const { current } = inputRef;
    if (current) current.focus();
    setValue('');
    supressFocusEvent.current = false;
    trackEvent({ action: 'reset' });
  };

  const handleFocus = () => {
    if (value !== '') setShowSearchResults(true);
    props.onFocus && props.onFocus();
  };

  return (
    <SearchWrapper ref={containerRef}>
      <Textfield
        ref={inputRef}
        name={props.name}
        type="search"
        disabled={props.disabled}
        error={props.error}
        value={value}
        placeholder={props.placeholder}
        prefix={<SearchIcon />}
        postfix={
          <SearchFieldPostfix
            styleType={props.light ? 'dark' : 'light'}
            active={showSearchResults}
            onReset={handleReset}
            onFocus={() => {
              supressFocusEvent.current = true;
            }}
            searchQuery={value}
            searchStatus={props.status}
          />
        }
        onChange={(event) => {
          setValue(event.currentTarget.value);
        }}
        onFocus={() => {
          if (!supressFocusEvent.current) {
            /** only track focus here to avoid double focus event */
            trackEvent({
              action: 'focus',
            });
          }
          handleFocus();
        }}
        autoComplete="off"
        onBlur={handleClose}
      />
      <Popper
        anchorEl={inputRef.current}
        open={showSearchResults}
        placement="bottom-start"
        role="listbox"
        wrapper={(child) => child}
        blockPointerEvents={false}
        zIndex={999} // yolo
        animationDuration={animationDuration}
      >
        <SearchDrop ref={childRef} onClick={handleBlur}>
          <ScrollPane>
            <SearchResults data-cy-id="search-results-list">
              {props.children}
            </SearchResults>
          </ScrollPane>
        </SearchDrop>
      </Popper>
    </SearchWrapper>
  );
};

DesktopSearch.defaultProps = {
  name: 'search-search-field',
  error: '',
  placeholder: '150k companies worldwide',
  query: '',
};
